import { RefObject, useEffect } from "react"
import Moveable from "react-moveable"

const HIDDEN = "data-repeated-distance"

/**
 * Moveable draws a gap's distance once for every edge the objects share (left, centre, right),
 * so objects of the same size show it two or three times. Only the middle one is kept, as
 * design tools show it once.
 */
const hideRepeatedDistances = (box: HTMLElement) => {
  const gaps = new Map<string, { element: HTMLElement; across: number }[]>()

  box.querySelectorAll<HTMLElement>(".moveable-guideline-group").forEach((group) => {
    group.removeAttribute(HIDDEN)
    const rect = group.getBoundingClientRect()
    const vertical = group.classList.contains("moveable-vertical")
    const along = vertical ? [rect.top, rect.height] : [rect.left, rect.width]
    const key = [vertical, group.textContent, ...along.map(Math.round)].join("|")
    const same = gaps.get(key) ?? []
    same.push({ element: group, across: vertical ? rect.left : rect.top })
    gaps.set(key, same)
  })

  gaps.forEach((same) => {
    if (same.length < 2) return
    const sorted = [...same].sort((a, b) => a.across - b.across)
    const kept = sorted[Math.floor((sorted.length - 1) / 2)]
    sorted.forEach(({ element }) => {
      if (element !== kept.element) element.setAttribute(HIDDEN, "")
    })
  })
}

export const repeatedDistanceStyles = { [`& [${HIDDEN}]`]: { display: "none" } }

/** Keeps one distance per gap in what `moveable` draws. */
const useOneDistancePerGap = (moveableRef: RefObject<Moveable>) => {
  useEffect(() => {
    const box = moveableRef.current?.getControlBoxElement()
    if (!box) return undefined

    const observer = new MutationObserver(() => hideRepeatedDistances(box))
    observer.observe(box, {
      subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ["style"],
    })

    return () => observer.disconnect()
  }, [moveableRef])
}

export default useOneDistancePerGap
